"use client";

import React, { useState } from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { Modal } from "antd";
import PopoverInfo from "./PopoverInfo";
import { toast } from "react-toastify";

const allowedFormats = [".mp4", ".mkv", ".webm", ".x-m4v", ".mov", ".ogg"];

const MediaShowcase = ({
  values,
  errors,
  setFieldValue,
  isEnglish,
}) => {
  const [currentVideo, setCurrentVideo] = useState(null);
  const [isPreviewModal, setIsPreviewModal] = useState(false);

  const t = {
    performanceTitle: isEnglish ? "Performance Videos (Minimum 3)*" : "प्रदर्शन वीडियो (कम से कम 3)*",
    performanceInfo: isEnglish
      ? "Upload a high-quality front-facing video of your past performance on a big stage. This helps participants understand your proficiency as a judge."
      : "अपने पिछले स्टेज प्रदर्शन का उच्च गुणवत्ता वाला फ्रंट-फेसिंग वीडियो अपलोड करें। यह प्रतिभागियों को आपकी क्षमता समझने में मदद करेगा।",
    performanceDesciption: isEnglish
      ? "Upload at least 3 work samples. You have 48 hrs after contest close to review submissions."
      : "कम से कम 3 कार्य नमूने अपलोड करें। प्रतियोगिता बंद होने के बाद सबमिशन की समीक्षा करने के लिए आपके पास 48 घंटे होते हैं।",
    uploadText: isEnglish ? "Upload" : "अपलोड करें",
    uploadedCount: (values.performance?.length || 0) + "/3 " + (isEnglish ? "Uploaded" : "अपलोड किया गया"),

    introTitle: isEnglish ? "Introductory Video (1–2 minutes)*" : "परिचयात्मक वीडियो (1–2 मिनट)*",
    introInfo: isEnglish
      ? "Record a short video introducing yourself, your expertise, and your experience. You can refer to the sample videos below for guidance."
      : "अपना परिचय, विशेषज्ञता और अनुभव बताते हुए एक छोटा वीडियो रिकॉर्ड करें। मार्गदर्शन के लिए नीचे दिए गए उदाहरण वीडियो देखें।",
    uploadIntroText: isEnglish ? "Upload video" : "वीडियो अपलोड करें",
    videoTooLarge: isEnglish
      ? "Video size should be less than 300 MBs."
      : "वीडियो का आकार 300 एमबी से कम होना चाहिए।",
    invalidFormat: isEnglish
      ? "Invalid format! Only mp4, mkv, webm, x-m4v, mov, and ogg are supported."
      : "अवैध प्रारूप! केवल mp4, mkv, webm, x-m4v, mov और ogg समर्थित हैं।",
    cameraMicAvailable: isEnglish
      ? "🎥 Camera & 🎙️ mic available for promotional content. (optional)"
      : "🎥 प्रचार सामग्री के लिए कैमरा और 🎙️ माइक्रोफोन उपलब्ध हैं। (वैकल्पिक)",
    allowPromotion: isEnglish
      ? "📣🎥🎬 Yes - I’d like the Feedants team to create/promote an ad featuring me. (optional)"
      : "📣🎥🎬 हाँ – मैं चाहता हूँ कि Feedants टीम मेरा फ़ीचर करते हुए एक विज्ञापन बनाए/प्रमोट करे। (वैकल्पिक)",
    subscribeUpdates: isEnglish
      ? "I’d like to subscribe to Judge‑only updates & tips. (optional)"
      : "मैं केवल जज-विशेष अपडेट्स और टिप्स प्राप्त करने के लिए सब्सक्राइब करना चाहूंगा। (वैकल्पिक)"
  };

  const openPreviewModal = () => setIsPreviewModal(true);
  const closePreviewModal = () => {
    setIsPreviewModal(false);
    setCurrentVideo(null);
  }

  const handleAddVideos = (e) => {
    const files = Array.from(e.target.files || []);
    const validVideos = [];

    files.forEach((file) => {
      const sizeInMB = file.size / (1024 * 1024);
      if (sizeInMB > 250) {
        toast.error(t.videoTooLarge);
        return;
      }
      const isValid = allowedFormats.some((ext) =>
        file.name.toLowerCase().includes(ext)
      );
      if (!isValid) {
        toast.error(t.invalidFormat);
        return;
      }
      validVideos.push({
        file,
        uri: URL.createObjectURL(file),
      });
    });

    const updated = [...(values.performance || []), ...validVideos];
    setFieldValue("performance", updated);
  };

  const handleRemoveVideo = (uri) => {
    const updated = (values.performance || []).filter((item) => item.uri !== uri);
    setFieldValue("performance", updated);
  };

  const handlePlay = (uri, title) => {
    setCurrentVideo({ uri, title });
    openPreviewModal();
  };

  const handlePickIntro = (e) => {
    const file = e.target.files?.[0];
    if (!file) {return;}

    const sizeInMB = file.size / (1024 * 1024);
    if (sizeInMB > 250) {
      toast.error(t.videoTooLarge);
      return;
    }

    const isValid = allowedFormats.some((ext) =>
      file.name.toLowerCase().includes(ext)
    );
    if (!isValid) {
      toast.error(t.invalidFormat);
      return;
    }

    const newIntro = {
      file,
      uri: URL.createObjectURL(file),
      slug: file.name,
      type: file.type,
    };

    setFieldValue("intro", newIntro);
  };

  return (
    <div className="space-y-6">
      {/* Performance Section */}
      <div className="mt-3">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-medium">{t.performanceTitle}</h3>
          <PopoverInfo infoText={t.performanceInfo} type="performanceVideo" />
        </div>
        <p className="text-gray-600 text-sm mb-2">
          {t.performanceDesciption}
        </p>

        <div className="overflow-x-auto mt-2">
          <div className="flex gap-2 whitespace-nowrap">
            <label className="min-w-[8rem] w-32 h-24 border border-gray-300 flex flex-col items-center justify-center rounded-lg cursor-pointer shrink-0 transition-transform duration-150 active:scale-95">
              <input
                type="file"
                accept="video/*"
                multiple
                className="hidden"
                onChange={handleAddVideos}
              />
              <AddIcon className="size-2 text-[#008080]" />
              <span className="text-gray-600 text-sm mt-1">{t.uploadText}</span>
            </label>

            {(values.performance || []).map(({ uri }) => (
              <div
                key={uri}
                className="relative min-w-[8rem] w-32 h-24 rounded-lg overflow-hidden border border-gray-300 shrink-0"
              >
                <video src={uri} className="w-full h-full object-cover" />
                <button
                  onClick={() => handlePlay(uri, "Peformance Video")}
                  className="absolute inset-0 flex justify-center items-center"
                >
                  <PlayArrowIcon className="!w-5 !h-5 text-white bg-black/50 p-1 rounded-full" />
                </button>
                <button
                  onClick={() => handleRemoveVideo(uri)}
                  className="absolute top-1 right-1 bg-red-100 text-red-600 px-1 rounded-full"
                >
                  <DeleteOutlineIcon className="!w-4 !h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {errors?.performance && (
          <p className="text-red-500 text-sm mt-1">{errors.performance}</p>
        )}
      </div>

      {/* Intro Section */}
      <div>
        <div className="flex items-center gap-2">
          <h3 className="text-base font-medium">{t.introTitle}</h3>
          <PopoverInfo infoText={t.introInfo} type="introVideo" />
        </div>

        {values.intro ? (
          <div className="relative w-full mt-2">
            <video
              src={values.intro.uri}
              className="w-full h-48 object-cover rounded-lg"
            />
            <button
              onClick={() => handlePlay(values.intro.uri, "Intro Video")}
              className="absolute inset-0 flex justify-center items-center"
            >
              <PlayArrowIcon className="!w-5 !h-5 text-white bg-black/50 p-1 rounded-full" />
            </button>
            <button
              onClick={() => setFieldValue("intro", null)}
              className="absolute top-1 right-1 bg-red-100 text-red-600 px-1 rounded-full"
            >
              <DeleteOutlineIcon className="!w-5 !h-5" />
            </button>
          </div>
        ) : (
          <div className="mt-2">
            <label className="w-full h-48 border border-gray-300 flex flex-col items-center justify-center rounded-lg cursor-pointer transition-transform duration-150 active:scale-95">
              <input
                type="file"
                accept="video/*"
                className="hidden"
                onChange={handlePickIntro}
              />
              <AddIcon className="size-2 text-[#008080]" />
              <span className="text-gray-600 mt-1">{t.uploadIntroText}</span>
            </label>
          </div>
        )}

        {errors?.intro && (
          <p className="text-red-500 text-sm mt-1">{errors.intro}</p>
        )}
      </div>

      {/* Optional Checkboxes */}
      <div className="space-y-2 pt-4 mt-6">
        <label className="flex items-start gap-2 font-medium text-gray-700">
          <input
            type="checkbox"
            checked={values.cameraMicAvailable}
            onChange={(e) =>
              setFieldValue("cameraMicAvailable", e.target.checked)
            }
            className="mt-1 accent-teal-600"
          />
          {t.cameraMicAvailable}
        </label>

        <label className="flex items-start gap-2 font-medium text-gray-700">
          <input
            type="checkbox"
            checked={values.allowPromotion}
            onChange={(e) =>
              setFieldValue("allowPromotion", e.target.checked)
            }
            className="mt-1 accent-teal-600"
          />
          {t.allowPromotion}
        </label>

        <label className="flex items-start gap-2 font-medium text-gray-700">
          <input
            type="checkbox"
            checked={values.subscribeUpdates}
            onChange={(e) =>
              setFieldValue("subscribeUpdates", e.target.checked)
            }
            className="mt-1 accent-teal-600"
          />
          {t.subscribeUpdates}
        </label>
      </div>

      {/* Preview Modal */}
      {currentVideo && (
        <Modal
          open={isPreviewModal}
          onCancel={closePreviewModal}
          footer={null}
          centered
          title={currentVideo.title}
          width="auto"
          styles={{
            padding: 14,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <div className="max-h-[80vh] w-full flex justify-center items-center">
            <video
              src={currentVideo.uri}
              controls
              className="max-h-[80vh] w-auto rounded-md"
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default MediaShowcase;
